import React, { useMemo, useRef, useState } from "react";
import {
  CloudArrowUp,
  FileCsv,
  FileText,
  ImageSquare,
  MusicNote,
  Eye,
  CheckCircle,
  XCircle,
  ArrowCounterClockwise,
  Lightning,
  Download,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const ACCEPT = "image/*,audio/*,.csv,.json,text/csv,application/json";

function classify(file) {
  const name = (file.name || "").toLowerCase();
  const ct = (file.type || "").toLowerCase();
  if (ct.startsWith("image/") || /\.(png|jpe?g|webp|gif)$/.test(name)) return "image";
  if (ct.startsWith("audio/") || /\.(mp3|wav|m4a|ogg|aac|mp4)$/.test(name)) return "audio";
  if (name.endsWith(".csv") || name.endsWith(".json") || ct === "text/csv" || ct === "application/json") return "metadata";
  return "unknown";
}

function fmtBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

const SAMPLE_CSV = `rank,song_title,artist_name,bio,video_url,instagram,spotify,youtube,website
1,Arch City Anthem,Almight E,Bio for Almight E.,,almighte_,,almightevevo,
2,IM SO STL,TBA,Bio goes here,,,,,
`;

const SAMPLE_JSON = JSON.stringify(
  [
    {
      rank: 1,
      song_title: "Arch City Anthem",
      artist_name: "Almight E",
      bio: "Bio text...",
      video_url: "",
      audio_filename: "01_arch_city.mp3",
      image_filename: "01_arch_city.jpg",
      social_links: [
        { platform: "Spotify", url: "https://open.spotify.com/artist/..." },
        { platform: "Instagram", url: "https://instagram.com/almighte_" },
      ],
    },
  ],
  null,
  2,
);

function downloadTemplate(kind) {
  const blob = new Blob([kind === "json" ? SAMPLE_JSON : SAMPLE_CSV], {
    type: kind === "json" ? "application/json" : "text/csv",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = kind === "json" ? "slots_template.json" : "slots_template.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function FileChip({ file, onRemove }) {
  const k = classify(file);
  const Icon = k === "image" ? ImageSquare : k === "audio" ? MusicNote : k === "metadata" ? (file.name.endsWith(".csv") ? FileCsv : FileText) : FileText;
  const tone =
    k === "image" ? "text-neon-cyan border-neon-cyan/30 bg-neon-cyan/5"
    : k === "audio" ? "text-neon-magenta border-neon-magenta/30 bg-neon-magenta/5"
    : k === "metadata" ? "text-amber-300 border-amber-400/30 bg-amber-500/5"
    : "text-zinc-400 border-ink-600 bg-ink-900";
  return (
    <div className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md border text-xs font-mono ${tone}`} data-testid={`batch-file-chip-${file.name}`}>
      <Icon size={14} weight="bold" />
      <span className="truncate max-w-[220px]">{file.name}</span>
      <span className="text-[10px] opacity-60">{fmtBytes(file.size)}</span>
      <button
        onClick={onRemove}
        className="ml-1 text-zinc-500 hover:text-neon-magenta"
        data-testid={`batch-file-remove-${file.name}`}
        title="Remove from batch"
      >
        <XCircle size={14} weight="bold" />
      </button>
    </div>
  );
}

function PreviewRow({ row }) {
  const ok = row.applied;
  const planned = row.image_filename || row.audio_filename || row.song_title || row.artist_name;
  return (
    <tr className="border-b border-ink-700/60 last:border-0" data-testid={`batch-preview-row-${row.rank}`}>
      <td className="px-3 py-2 font-mono text-neon-cyan text-xs">#{String(row.rank).padStart(2, "0")}</td>
      <td className="px-3 py-2 text-sm text-white">{row.song_title || <span className="text-zinc-600">—</span>}</td>
      <td className="px-3 py-2 text-sm text-zinc-300">{row.artist_name || <span className="text-zinc-600">—</span>}</td>
      <td className="px-3 py-2 text-xs font-mono text-zinc-400">
        {row.image_filename ? row.image_filename : <span className="text-zinc-600">— no artwork —</span>}
      </td>
      <td className="px-3 py-2 text-xs font-mono text-zinc-400">
        {row.audio_filename ? row.audio_filename : <span className="text-zinc-600">— no audio —</span>}
      </td>
      <td className="px-3 py-2 text-xs">
        {ok ? (
          <span className="inline-flex items-center gap-1 text-emerald-400"><CheckCircle size={14} weight="fill" /> applied</span>
        ) : planned ? (
          <span className="inline-flex items-center gap-1 text-amber-300"><Eye size={14} weight="bold" /> ready</span>
        ) : (
          <span className="text-zinc-600">empty</span>
        )}
        {row.errors?.length ? (
          <div className="text-[10px] text-neon-magenta mt-1">{row.errors.join("; ")}</div>
        ) : null}
      </td>
    </tr>
  );
}

export default function BatchImportPanel({ onApplied }) {
  const [files, setFiles] = useState([]);
  const [plan, setPlan] = useState(null); // dry-run response
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState("idle"); // 'idle' | 'previewing' | 'applying'
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const stats = useMemo(() => {
    let img = 0, aud = 0, meta = 0, unk = 0;
    files.forEach((f) => {
      const k = classify(f);
      if (k === "image") img++;
      else if (k === "audio") aud++;
      else if (k === "metadata") meta++;
      else unk++;
    });
    return { img, aud, meta, unk };
  }, [files]);

  const addFiles = (incoming) => {
    const list = Array.from(incoming || []);
    setFiles((prev) => {
      const seen = new Set(prev.map((f) => `${f.name}-${f.size}`));
      const merged = [...prev];
      for (const f of list) {
        const key = `${f.name}-${f.size}`;
        if (!seen.has(key)) {
          merged.push(f);
          seen.add(key);
        }
      }
      return merged;
    });
    setPlan(null);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
    setPlan(null);
  };

  const reset = () => {
    setFiles([]);
    setPlan(null);
    setPhase("idle");
    if (inputRef.current) inputRef.current.value = "";
  };

  const buildForm = () => {
    const form = new FormData();
    files.forEach((f) => form.append("files", f, f.name));
    return form;
  };

  const runPreview = async () => {
    if (!files.length) {
      toast.error("Drop some files first");
      return;
    }
    setBusy(true);
    setPhase("previewing");
    try {
      const { data } = await api.post("/admin/slots/batch-import?dry_run=true", buildForm(), {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPlan(data);
      if (!data.rows?.length) {
        toast.warning("No slots resolved from your files. Check the metadata file or filename prefixes.");
      } else {
        toast.success(`Preview ready: ${data.rows.length} slot(s) detected`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const runApply = async () => {
    if (!files.length) {
      toast.error("Nothing to apply");
      return;
    }
    setBusy(true);
    setPhase("applying");
    try {
      const { data } = await api.post("/admin/slots/batch-import", buildForm(), {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPlan(data);
      toast.success(`Applied ${data.applied_count}/${data.total_rows} slot(s)`);
      if (onApplied) onApplied(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="batch-import-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="text-xs font-bold tracking-[0.3em] text-neon-cyan uppercase mb-2">Batch Import</p>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tighter">Drop a folder. Update all 10 in one shot.</h2>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            Drop artwork, audio, and a CSV or JSON metadata file together. Files are matched to slots either by the
            <span className="text-zinc-300"> filename in the metadata</span> or by a
            <span className="text-zinc-300"> rank prefix</span> like <code className="font-mono text-neon-cyan">01_song.mp3</code>.
            Slots not in the batch are <span className="text-zinc-300">left untouched</span>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => downloadTemplate("csv")}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-ink-600 text-zinc-300 hover:text-neon-cyan hover:border-neon-cyan/50 text-xs"
            data-testid="batch-download-csv"
          >
            <Download size={14} weight="bold" /> CSV template
          </button>
          <button
            onClick={() => downloadTemplate("json")}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-ink-600 text-zinc-300 hover:text-neon-cyan hover:border-neon-cyan/50 text-xs"
            data-testid="batch-download-json"
          >
            <Download size={14} weight="bold" /> JSON template
          </button>
        </div>
      </div>

      {/* Module Setting Boxes */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <SettingBox
          title="Artist"
          color="text-neon-cyan"
          testid="batch-module-artist"
          lines={[
            "rank, artist_name, bio",
            "social_links (CSV: instagram, twitter, youtube, spotify, soundcloud, tiktok, website)",
          ]}
        />
        <SettingBox
          title="Website / Links"
          color="text-amber-300"
          testid="batch-module-website"
          lines={[
            "video_url (YouTube link)",
            "Any URL column in CSV becomes a social link badge.",
          ]}
        />
        <SettingBox
          title="Text Names"
          color="text-neon-magenta"
          testid="batch-module-text"
          lines={[
            "song_title (a.k.a. title / song)",
            "image_filename, audio_filename — must match a dropped file exactly.",
          ]}
        />
      </div>

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`relative rounded-xl border-2 border-dashed transition-colors cursor-pointer p-10 text-center ${
          dragOver ? "border-neon-cyan bg-neon-cyan/5" : "border-ink-600 bg-ink-900/60 hover:border-neon-cyan/60"
        }`}
        data-testid="batch-dropzone"
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => addFiles(e.target.files)}
          data-testid="batch-file-input"
        />
        <div className="flex flex-col items-center gap-3">
          <CloudArrowUp size={42} weight="bold" className="text-neon-cyan" />
          <div className="text-white font-semibold">Drop files here, or click to browse</div>
          <div className="text-xs text-zinc-500 font-mono">
            images · audio · one .csv or .json — up to 25MB each
          </div>
        </div>
      </div>

      {/* File list + stats */}
      {files.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="flex items-center gap-3 text-xs font-mono text-zinc-400">
              <span data-testid="batch-stats-total">{files.length} file{files.length === 1 ? "" : "s"}</span>
              <span className="text-zinc-600">·</span>
              <span className="text-neon-cyan" data-testid="batch-stats-img">{stats.img} artwork</span>
              <span className="text-neon-magenta" data-testid="batch-stats-aud">{stats.aud} audio</span>
              <span className="text-amber-300" data-testid="batch-stats-meta">{stats.meta} metadata</span>
              {stats.unk > 0 && <span className="text-zinc-500">{stats.unk} unknown</span>}
            </div>
            <button
              onClick={reset}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-ink-600 text-zinc-300 hover:text-neon-magenta hover:border-neon-magenta/50 text-xs"
              data-testid="batch-reset"
            >
              <ArrowCounterClockwise size={14} weight="bold" /> Reset
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {files.map((f, i) => (
              <FileChip key={`${f.name}-${f.size}-${i}`} file={f} onRemove={() => removeFile(i)} />
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      {files.length > 0 && (
        <div className="mt-6 flex items-center gap-3 flex-wrap">
          <button
            onClick={runPreview}
            disabled={busy}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-ink-600 text-zinc-200 hover:text-neon-cyan hover:border-neon-cyan/50 text-sm disabled:opacity-50"
            data-testid="batch-preview-btn"
          >
            <Eye size={16} weight="bold" />
            {busy && phase === "previewing" ? "Previewing..." : "Preview mapping"}
          </button>
          <button
            onClick={runApply}
            disabled={busy}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-neon-cyan text-black text-sm font-semibold hover:bg-neon-cyanHover disabled:opacity-50"
            data-testid="batch-apply-btn"
          >
            <Lightning size={16} weight="fill" />
            {busy && phase === "applying" ? "Applying..." : "Apply to slots"}
          </button>
        </div>
      )}

      {/* Plan / results table */}
      {plan && (
        <div className="mt-8 rounded-xl border border-ink-600 bg-ink-900/40 overflow-hidden" data-testid="batch-plan-table">
          <div className="px-4 py-3 border-b border-ink-600 flex items-center justify-between text-xs font-mono text-zinc-400">
            <span>
              {plan.dry_run ? "PREVIEW" : "RESULT"} · metadata: {plan.metadata_file || <span className="text-zinc-600">none</span>} · {plan.total_rows} slot(s)
              {plan.dry_run ? "" : ` · ${plan.applied_count} applied`}
            </span>
            {plan.unmatched_files?.length > 0 && (
              <span className="text-amber-300">{plan.unmatched_files.length} unmatched file(s)</span>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 bg-ink-800/40">
                <tr>
                  <th className="px-3 py-2">Rank</th>
                  <th className="px-3 py-2">Title</th>
                  <th className="px-3 py-2">Artist</th>
                  <th className="px-3 py-2">Artwork</th>
                  <th className="px-3 py-2">Audio</th>
                  <th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {plan.rows?.map((row) => <PreviewRow key={row.rank} row={row} />)}
              </tbody>
            </table>
          </div>
          {plan.unmatched_files?.length > 0 && (
            <div className="px-4 py-3 border-t border-ink-600 text-xs text-zinc-400">
              <div className="font-bold tracking-[0.2em] uppercase text-amber-300 mb-1">Unmatched files</div>
              <div className="font-mono text-zinc-500 break-words">{plan.unmatched_files.join(", ")}</div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function SettingBox({ title, color, lines, testid }) {
  return (
    <div className="rounded-lg border border-ink-600 bg-ink-800/40 p-4" data-testid={testid}>
      <div className={`text-[10px] font-bold tracking-[0.3em] uppercase mb-2 ${color}`}>{title}</div>
      <ul className="text-xs text-zinc-400 space-y-1.5 font-mono leading-relaxed">
        {lines.map((l) => <li key={l}>· {l}</li>)}
      </ul>
    </div>
  );
}
